/**
 * The tank's geometry, in one place: the physics builds its walls from it, the water its grid,
 * the page its ring count, and the glass frame drawn over the scene follows the same lines.
 * Fractions are of the viewport; pixel helpers take the viewport in CSS pixels.
 */

/** The walls sit this far in from the tank's sides (a fraction of its width), the ceiling this far below the top (of the height): at the edges, the glass panes are the walls. */
export const TANK_SIDE_INSET = 0
export const TANK_TOP_INSET = 0
/**
 * The top corners are rounded, with this radius as a fraction of the tank's height: about two
 * ring widths, so a ring carried up a wall follows the water round instead of being pressed into
 * a corner, and small enough not to show. The physics tank and the water use the same radius.
 */
export const CORNER_RADIUS = 0.12
/**
 * The tank is never wider than this many times the title: on an ultra-wide screen the water and
 * the pile stay around the letters, where the game is, and the sides are back panel. On a
 * normal screen this is wider than the viewport, so the tank is the viewport.
 */
export const TANK_TITLE_FACTOR = 1.7

/**
 * The title's width in CSS pixels for a viewport: the same rule as the hero column's CSS
 * (`w-[min(88vw,140vh)] md:… lg:… short:… max-w-[1440px]` in App.tsx), kept here so the tank
 * can follow the title without reading the DOM.
 */
export function titleWidthPx(width: number, height: number): number {
  if (height <= 480) return Math.min(0.88 * width, 0.95 * height)
  const share = width >= 1024 ? 0.7 : width >= 768 ? 0.8 : 0.88
  return Math.min(share * width, 1.4 * height, 1440)
}

/** The tank's width in CSS pixels for a viewport, before the walls' inset. */
export function tankWidthPx(width: number, height: number): number {
  return Math.min(width, titleWidthPx(width, height) * TANK_TITLE_FACTOR)
}

/** Where the tank's walls fall on screen, in CSS pixels; the floor is the bottom edge. */
export function tankRectPx(width: number, height: number): { left: number; right: number; top: number } {
  const w = tankWidthPx(width, height)
  const inner = w * (1 - 2 * TANK_SIDE_INSET)
  return { left: (width - inner) / 2, right: (width + inner) / 2, top: height * TANK_TOP_INSET }
}
